import React, { useEffect, useRef, useState } from "react";
import { FileCheck, Upload, CheckCircle2, Clock, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PageHeader, Section, StatusBadge, LoadingBlock } from "../../components/app/ui";
import { useAuth } from "../../context/AuthContext";
import { kycApi, driverApi, imageApi, authApi, API_BASE, errorMessage, formatDate } from "../../lib/api";
import { prepareDocument } from "../../lib/files";
import { DateField } from "../../components/app/DateField";
import { driverType, categoryOf, isCommercialCategory, TAXI_PERMIT_DOC, markPermitUploaded, permitUploadedAt } from "../../lib/driver";

const COPY = {
  unverified: { icon: FileCheck, title: "Upload your documents", text: "Upload each required document below. Our team reviews submissions within 24 hours." },
  pending: { icon: Clock, title: "Under review", text: "All documents received. We will notify you as soon as the review is complete." },
  verified: { icon: CheckCircle2, title: "You are verified", text: "Your account is fully verified. You can go online and accept work." },
  rejected: { icon: XCircle, title: "Verification rejected", text: "Please review the reason below, re-upload the affected document and we will take another look." },
};

function DocRow({ doc, onUploaded, rekycDue }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const refreshVehicle = doc.doc_type === "vehicle_photo" && rekycDue;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const prepared = await prepareDocument(file);
      if (doc.doc_type === "vehicle_photo") {
        // Public rider-facing image, not a private KYC doc.
        const res = await imageApi.upload(prepared.base64, prepared.mime);
        const url = res.url?.startsWith("http") ? res.url : `${API_BASE}${res.url}`;
        await authApi.updateProfile({ vehicle_photo: url });
      } else {
        await kycApi.upload({ doc_type: doc.doc_type, file_data: prepared.base64, mime: prepared.mime, file_name: prepared.name });
        if (doc.doc_type === "taxi_permit") markPermitUploaded();
      }
      toast.success(`${doc.label} uploaded`, prepared.note ? { description: prepared.note } : undefined);
      onUploaded();
    } catch (e) {
      const msg = e?.response ? errorMessage(e, "Upload failed") : e?.message || "Upload failed";
      toast.error(/larger than \d+ MB/.test(msg) ? "This document is too large for the server. Please upload a smaller file (under 10 MB)." : msg.includes("Only JPEG or PNG") ? "The server could not read this file. Please upload a JPG/PNG photo or a PDF of the document." : msg);
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <li className="flex flex-wrap items-center gap-4 rounded-2xl border border-surface-300 bg-white p-4" data-testid={`doc-${doc.doc_type}`}>
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${doc.uploaded ? "bg-graphite-50 text-graphite" : "bg-surface-200 text-ink-muted"}`}>{doc.uploaded ? <CheckCircle2 className="h-5 w-5" /> : <FileCheck className="h-5 w-5" />}</span>
      {doc.preview_url && <img src={doc.preview_url} alt="Vehicle" className="h-11 w-16 shrink-0 rounded-lg border border-surface-300 object-cover" data-testid="vehicle-photo-preview" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{doc.label}</p>
        <p className="text-xs text-ink-soft">{doc.uploaded ? `Uploaded${doc.uploaded_at ? ` ${formatDate(doc.uploaded_at)}` : ""}${doc.file_name ? ` · ${doc.file_name}` : ""}` : "JPG, PNG or PDF up to 10 MB — PDFs are converted and large photos compressed automatically."}</p>
        {refreshVehicle && <p className="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800" data-testid="vehicle-photo-refresh-nudge">Re-KYC due — please retake your vehicle photo so riders always see your current cab.</p>}
      </div>
      <label className="inline-flex">
        <input ref={ref} type="file" accept={doc.doc_type === "vehicle_photo" ? "image/jpeg,image/png,image/webp,image/heic,image/heif" : "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`doc-file-${doc.doc_type}`} />
        <Button asChild variant={doc.uploaded && !refreshVehicle ? "outline" : "default"} className={`cursor-pointer rounded-full ${doc.uploaded && !refreshVehicle ? "" : "bg-brand text-white hover:bg-brand-600"}`}>
          <span>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{refreshVehicle ? "Refresh" : doc.uploaded ? "Replace" : "Upload"}</span>
        </Button>
      </label>
    </li>
  );
}

function PermitForm({ user, onSaved }) {
  const [number, setNumber] = useState(user?.taxi_permit_number || "");
  const [expiry, setExpiry] = useState(user?.taxi_permit_expiry ? String(user.taxi_permit_expiry).slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const status = user?.taxi_permit_status || "none";
  const save = async () => {
    if (!number.trim() || !expiry) { toast.error("Enter your permit number and expiry date."); return; }
    setBusy(true);
    try {
      await driverApi.permit({ taxi_permit_number: number.trim(), taxi_permit_expiry: expiry });
      toast.success("Permit details saved — pending review");
      onSaved();
    } catch (e) { toast.error(errorMessage(e, "Could not save permit")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-2xl border border-surface-300 bg-surface-50 p-4" data-testid="permit-form">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink"><ShieldCheck className="h-4 w-4 text-brand" /> Commercial Taxi Permit</span>
        {status !== "none" && <StatusBadge status={status} />}
      </div>
      <p className="mt-1 text-xs text-ink-soft">Enter your permit details, then upload a photo of the permit below. Rides unlock once this is approved.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Permit number</Label><Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g. NL01/PERMIT/1234" className="h-10" data-testid="permit-number" /></div>
        <div className="space-y-1.5"><Label>Expiry date</Label><DateField value={expiry} onChange={setExpiry} disablePast fromYear={new Date().getFullYear()} toYear={new Date().getFullYear() + 20} testId="permit-expiry" /></div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="permit-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save permit details</Button>
    </div>
  );
}

export default function KycStatus({ embedded = false }) {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const load = () => kycApi.mine().then(setData).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  const onUploaded = async () => { await load(); refresh(); };

  if (!data) return embedded ? <LoadingBlock rows={3} /> : <><PageHeader title="KYC status" /><LoadingBlock rows={3} /></>;
  const status = data.status || user?.verification_status || "unverified";
  const c = COPY[status] || COPY.unverified;
  const needsPermit = user?.role === "driver" && (isCommercialCategory(categoryOf(user)) || driverType(user) === "taxi");
  const documents = (() => {
    const list = [...(data.documents || [])];
    if (needsPermit && !list.some((d) => d.doc_type === "taxi_permit")) {
      const at = permitUploadedAt();
      list.push({ ...TAXI_PERMIT_DOC, uploaded: !!at, uploaded_at: at });
    }
    return list;
  })();
  const uploaded = documents.filter((d) => d.uploaded).length;
  const total = documents.length;

  return (
    <>
      {!embedded && <PageHeader title="KYC status" description={user?.role === "driver" ? "Verification is required before you can go online." : "Verification is required before your restaurant is listed."} />}
      {(() => {
        const statusCard = (
          <div className={`kt-card p-6 ${status === "verified" ? "border-graphite-200" : status === "rejected" ? "border-red-200" : ""}`} data-testid="kyc-status-card">
            <div className="flex items-center justify-between"><span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${status === "verified" ? "bg-graphite text-white" : status === "rejected" ? "bg-red-50 text-red-700" : "bg-brand-50 text-brand"}`}><c.icon className="h-6 w-6" /></span><StatusBadge status={status} /></div>
            <h3 className="mt-5 text-lg font-semibold text-ink">{c.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{c.text}</p>
            {status === "rejected" && (data.rejected_reason || user?.verification_rejected_reason) && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800" data-testid="kyc-reason">Reason: {data.rejected_reason || user?.verification_rejected_reason}</p>}
            <ul className="mt-6 grid gap-2 text-xs text-ink-muted sm:grid-cols-2 xl:grid-cols-1">
              <li className="flex gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-graphite" /> Documents are encrypted and only visible to our verification team.</li>
              <li className="flex gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-graphite" /> Same status is shown in the mobile app.</li>
            </ul>
          </div>
        );
        const docsSection = (
          <Section title="Required documents" description={total ? `${uploaded} of ${total} uploaded` : "No documents required for this account"}>
            {needsPermit && <PermitForm user={user} onSaved={onUploaded} />}
            {total > 0 && (
              <div className="mb-5 h-2 overflow-hidden rounded-full bg-surface-200"><div className="h-full rounded-full bg-graphite transition-all duration-500" style={{ width: `${(uploaded / total) * 100}%` }} /></div>
            )}
            <ul className="space-y-3">{documents.map((d) => <DocRow key={d.doc_type} doc={d} onUploaded={onUploaded} rekycDue={user?.rekyc_due} />)}</ul>
          </Section>
        );
        // Embedded (inside Profile's column): stack full-width so document cards never get crushed
        // into a narrow nested column on small laptops. Standalone: side-by-side only on wide (xl) screens.
        return embedded ? (
          <div className="space-y-6">{statusCard}{docsSection}</div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[1fr_360px]">{docsSection}{statusCard}</div>
        );
      })()}
    </>
  );
}
